import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../context/AuthContext";

const navigation = [
  { name: "Dashboard", href: "/" },
  { name: "Add Permit", href: "/add-permit" },
  { name: "My Permits", href: "/my-permits" },
  { name: "How to Guide", href: "/how-to-guide" },
  { name: "Settings", href: "/settings" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const { profile } = useContext(AuthContext);
  const initials = `${profile?.first_name?.[0] || ""}${
    profile?.last_name?.[0] || ""
  }`.toUpperCase();

  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <Disclosure as="nav" className="bg-white shadow-sm border-b border-gray-100">
      {({ open, close }) => (
        <>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 justify-between items-center">
              {/* Mobile menu button */}
              <div className="flex items-center md:hidden">
                <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>

              {/* Logo and Brand Name */}
              <div className="flex items-center space-x-3">
                <img
                  src="/logo_assets/android-chrome-192x192.png"
                  alt="ResidentPass Logo"
                  className="h-8 w-auto"
                />
                <span className="text-xl font-bold text-customIndigo">
                  ResidentPass
                </span>
              </div>

              {/* Desktop Menu */}
              <div className="hidden md:flex md:items-center md:space-x-8">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        isActive
                          ? "text-customIndigo border-b-2 border-customIndigo"
                          : "text-gray-500 hover:text-customIndigo hover:border-b-2 hover:border-customIndigo",
                        "px-1 py-2 text-sm font-medium transition-colors duration-200"
                      )}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              {/* UserMenu */}
              <div className="flex items-center">
                <Menu as="div" className="relative">
                  <div>
                    <MenuButton className="flex h-8 w-8 items-center justify-center rounded-full bg-customIndigo text-sm font-medium text-white hover:bg-[rgb(45,100,112)] focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 transition-colors duration-200">
                      {initials || "U"}
                    </MenuButton>
                  </div>
                  <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                    <MenuItem>
                      {({ active }) => (
                        <Link
                          to="/settings"
                          className={classNames(
                            active ? "bg-gray-50" : "",
                            "block px-4 py-2 text-sm text-gray-700"
                          )}
                        >
                          Settings
                        </Link>
                      )}
                    </MenuItem>
                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={classNames(
                            active ? "bg-gray-50" : "",
                            "block w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700"
                          )}
                        >
                          Log out
                        </button>
                      )}
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          {/* Mobile Nav Panel */}
          <DisclosurePanel className="md:hidden">
            <div className="space-y-1 px-2 pb-3 pt-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={classNames(
                      isActive
                        ? "bg-gray-50 text-customIndigo"
                        : "text-gray-500 hover:bg-gray-50 hover:text-customIndigo",
                      "block rounded-md px-3 py-2 text-base font-medium"
                    )}
                    onClick={() => close()}
                  >
                    {item.name}
                  </Link>
                );
              })}
              <button
                onClick={() => {
                  handleLogout();
                  close();
                }}
                className="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-red-600 hover:bg-gray-50 hover:text-red-700"
              >
                Log out
              </button>
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
